import React, { useContext, useState, useRef, useEffect } from 'react';
import { Redirect } from 'react-router-dom';
import { Environment } from 'src/Environment';

import { businessCashAdvanceCalculationRoute, paymentsDashboardRoute } from 'src/routing/routes';
import Faqs from '../faqs/faqs';
import QualificationBox from '../qualification-box/qualification-box';
import QualificationSideScroll from '../qualification-side-scroll/qualification-side-scroll';
import { OfferContext } from 'src/context/offer-context';

import { formatCurrencyAmountSimple } from 'src/helpers/formatting';
import toastMessageServiceSingleton from 'src/services/toast-message.service';

import Disclaimer from '../disclaimer/disclaimer';
import HeaderNav from '../../header-nav/header-nav';

import TealiumEvents from 'src/helpers/tealium-events';
import { contacts } from 'src/common';
import useMerchantProfile from 'src/hooks/useMerchantProfile';

const faqs = [
  {
    title: 'Why is my business pre-approved for funding?',
    bulletPoints: [
      'Max inflow per month R 15 000',
      'Max outflow per month R 15 000',
      'Wallet limit R 20 000',
    ],
  },
  {
    title: 'How long before the cash is in my merchant account?',
    text: `You'll receive an email that the money has been disbursed to you, depending on where you bank this could take up to a 48 hours before the money is in your account.`,
  },
  {
    title: 'How will I repay my Advance?',
    text: 'This is an automated process and the daily repayment amount is based on the percentage you agree to, this is a daily repayment product ie if you agree to 15% then we will deduct 15% of all your POS transactions daily and allocate it to your Business Cash Advance balance.',
  },
  {
    title: 'How do I speak to a real person?',
    text: `Dial our call center on ${contacts.number}`,
  },
];

export default function BusinessCashAdvancePreApproved() {
  const [redirectPath, setRedirectPath] = useState<string>();
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const { offers } = useContext(OfferContext);
  const topOfPage = useRef<HTMLHeadingElement>(null);

  const { merchant } = useMerchantProfile();

  const checkOfferStatus = () => {
    if (window.innerWidth > 769) {
      toastMessageServiceSingleton.addToast({
        message: (
          <>
            Business Cash Advance available:{' '}
            <span className="has-text-weight-bold is-nowrap">
              R {formatCurrencyAmountSimple(offers?.offerDetails?.AvailableLimit)}
            </span>
          </>
        ),
        actionButtonText: 'Apply now',
        actionButtonOnClick: () => {
          TealiumEvents.infoPageBCAClickViewOffer(businessCashAdvanceCalculationRoute);

          setRedirectPath(businessCashAdvanceCalculationRoute);
        },
        permanent: true,
      });
    }
  };

  useEffect(() => {
    TealiumEvents.infoPageBCAPageLoad();

    checkOfferStatus();

    if (topOfPage.current) {
      topOfPage.current.scrollIntoView(false);
    }

    return () => {
      toastMessageServiceSingleton.removeAllToasts();
    };
  }, []);

  useEffect(() => {
    checkOfferStatus();
  }, [offers?.offerDetails?.QualificationStatus]);

  // TODO: BCA Flag to be removed
  if (Environment.current?.disabledFeatures.bca) {
    return <Redirect push to={paymentsDashboardRoute} />;
  }

  if (redirectPath) {
    return <Redirect push to={redirectPath} />;
  }

  return (
    <div>
      <HeaderNav
        title={merchant?.companyName ? `Hi, welcome back ${merchant?.companyName}` : ''}
        screenName="Business Cash Advance"
      >
        {showDisclaimerModal && <Disclaimer setShow={setShowDisclaimerModal} />}

        <h3 className="page-title" ref={topOfPage}>
          You're pre-approved for R{' '}
          {formatCurrencyAmountSimple(offers?.offerDetails?.AvailableLimit)}
        </h3>

        <p className="page-description">
          With Business Cash Advance, your repayments are linked to the amount of money you're
          earning every day. So <b>when you're not trading, you're not paying.</b>
        </p>

        <h1 className="secondary-title">Why you qualify for a Business Cash Advance:</h1>
        <QualificationSideScroll automationIdPrefix="Business_Cash_Advance_Pre_Approved_QualificationSideScroll">
          <QualificationBox
            qualificationTitle="Operating period"
            qualificationSubTitle="You've been trading for at least"
            qualificationMeasure="4 months"
          />
          <QualificationBox
            qualificationTitle="Turnover"
            qualificationSubTitle="Your business makes more than"
            qualificationMeasure="R 3000.00"
          />
          <QualificationBox
            qualificationTitle="Minimum payment transactions"
            qualificationSubTitle="On any of your POS terminals"
            qualificationMeasure="10 payments"
          />
        </QualificationSideScroll>
        <Faqs
          faqs={faqs}
          automationIdPrefix="Business_Cash_Advance_Pre_Approved_Faqs"
          faqOnClick={(topic) => TealiumEvents.infoPageBCAClickFAQ(topic)}
        />
        <div className="disclaimer">
          <span className="disclaimer-title">Disclaimer</span>
          <br />
          <span className="disclaimer-subtitle">
            By selecting 'View offer', you agree to the Vodacom{' '}
            <span
              className="is-red-text-link"
              automationid="BCA_Link_Disclaimer"
              onClick={() => {
                TealiumEvents.infoPageBCAClickDisclaimerLink('');

                setShowDisclaimerModal(true);
              }}
            >
              disclaimer
            </span>
            .
          </span>
        </div>

        {offers?.offerDetails?.QualificationStatus === 1 && (
          <div className="view-offer-button">
            <button
              automationid="BCA_Button_Get_Advance"
              className="button is-primary is-fullwidth"
              onClick={() => {
                TealiumEvents.infoPageBCAClickViewOffer(businessCashAdvanceCalculationRoute);

                setRedirectPath(businessCashAdvanceCalculationRoute);
              }}
            >
              View offer
            </button>
          </div>
        )}
      </HeaderNav>
    </div>
  );
}
